
import {
  MotionValue,
  motion,
  useMotionTemplate,
  useMotionValue,
} from 'framer-motion';
import React, { ReactNode, useId, useMemo } from 'react';

import {
  getDeterministicPattern,
  getRandomPattern,
} from '@/components/card/utils';
import { GridPattern, Pattern } from '@/components/grid-pattern';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export function CardHoverableGridPattern({
  children,
  className,
  index,
}: {
  children: ReactNode;
  className?: HTMLDivElement['className'];
  index: number;
}) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const pattern = useMemo(() => {
    if (index >= 0) {
      return getDeterministicPattern(index);
    }
    return getRandomPattern();
  }, [index]);

  function onMouseMove({
    currentTarget,
    clientX,
    clientY,
  }: React.MouseEvent<HTMLDivElement>) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <Card
      key={useId()}
      onMouseMove={onMouseMove}
      className={cn(
        'group relative bg-card/50 transition-shadow hover:shadow-md hover:shadow-zinc-900/5 pt-2',
        className,
      )}
    >
      <HoverableGridPattern {...pattern} mouseX={mouseX} mouseY={mouseY} />
      <div className="absolute inset-0 rounded-2xl ring-1 ring-border/20 ring-inset ring-border/7.5 group-hover:ring-border/10" />
      <div className="relative h-full">{children}</div>
    </Card>
  );
}

function HoverableGridPattern({
  mouseX,
  mouseY,
  ...gridProps
}: Pattern & {
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
}) {
  const maskImage = useMotionTemplate`radial-gradient(180px at ${mouseX}px ${mouseY}px, white, transparent)`;
  const style = { maskImage, WebkitMaskImage: maskImage };

  return (
    <div className="pointer-events-none">
      <div className="absolute inset-0 rounded-2xl transition duration-300 [mask-image:linear-gradient(white,transparent)] group-hover:opacity-50">
        <GridPattern
          width={72}
          height={56}
          x="50%"
          className="absolute inset-x-0 inset-y-[-30%] h-[160%] w-full skew-y-[-18deg] fill-black/[0.02] stroke-border/25"
          {...gridProps}
        />
      </div>
      <motion.div
        className="absolute inset-0 rounded-2xl bg-linear-to-r from-[#1CEFA5]/10 to-[#3C4568]/10 opacity-0 transition duration-300 group-hover:opacity-100"
        style={style}
      />
      <motion.div
        className="absolute inset-0 rounded-2xl opacity-0 mix-blend-overlay transition duration-300 group-hover:opacity-100"
        style={style}
      >
        <GridPattern
          width={72}
          height={56}
          x="50%"
          className="absolute inset-x-0 inset-y-[-30%] h-[160%] w-full skew-y-[-18deg] fill-black/50 stroke-black/70"
          {...gridProps}
        />
      </motion.div>
    </div>
  );
}
